/**
 * Member Profile. SPEC.md §7.3.
 *
 * One clean card, one edit button, no scattered controls.
 *
 * The rule that shapes this screen: the emergency PHONE NUMBER is not
 * rendered. The member sees the contact's name and relationship, plus a plain
 * explanation of why the number is hidden. They can still change it from the
 * edit sheet — they just cannot read anyone's, including their own, on a
 * screen someone could be looking over their shoulder at.
 */

import {
  ageFrom,
  bibNo,
  duesState,
  formatDate,
  initials,
  maskEmail,
  TIERS,
} from '../../data';
import { useClubData } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { ProfileSheet } from '../../components/sheets/ProfileSheet';
import { PasswordSheet } from '../../components/sheets/PasswordSheet';
import { Button, Empty, Fact, ViewTitle } from '../../components/ui';
import { useGrounds } from '../../context/ClubContext';

/* Repeated section chrome inside the profile card. */
const SEC = 'border-t border-line p-4';
const SEC_HEAD = 'mb-3 flex flex-wrap items-center justify-between gap-2.5';
const SEC_TITLE = 'font-market text-[9.5px] uppercase tracking-[0.13em] text-copy-muted';
const FACT_GRID = 'grid grid-cols-2 gap-x-[18px] gap-y-3.5 min-[560px]:grid-cols-3';
/* The soft explainer strip under a section (lock note, committee note). */
const LOCK = 'mt-3 flex items-start gap-3 border border-line bg-ground px-3.5 py-3';
const LOCK_ICON =
  'grid h-[26px] w-[26px] flex-none place-items-center border border-line bg-white text-copy-muted';
const LOCK_TEXT = 'text-[12.5px] leading-relaxed text-copy-muted [&_b]:font-semibold [&_b]:text-ink-700';

export function ProfilePage() {
  const grounds = useGrounds();
  const { db, me } = useClubData();
  const { openSheet } = useUI();

  if (!me) return <Empty title="No member">Sign in again to see your profile.</Empty>;

  const age = ageFrom(me.dob);
  const isMinor = age !== null && age < 18;
  const tier = TIERS[me.tier];

  return (
    <>
      <ViewTitle title="My profile" sub="Your details on the club roster" />

      <div className="mb-3.5 overflow-hidden border border-line bg-white">
        <div className="flex items-center gap-4 p-5">
          <div className="relative grid h-[78px] w-[78px] flex-none place-items-center overflow-hidden rounded-full border border-line bg-line-chip">
            {me.photo ? (
              <img src={me.photo} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="font-market text-[28px] font-extrabold text-copy-label">
                {initials(me.name)}
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-market text-[25px] font-extrabold leading-[1.05] tracking-[-0.03em]">
              {me.name}
            </div>
            <div className="mt-1.5 font-market text-[10.5px] leading-relaxed text-copy-muted">
              {tier.label} member · {grounds[me.ground] ?? '—'}
              <br />
              Member since {me.joined ? formatDate(me.joined) : '—'}
            </div>
            <div className="mt-2 inline-block bg-gold-50 px-2.5 py-1 font-market text-[15px] font-extrabold tracking-wider text-gold-800">
              BIB {bibNo(me.bib)}
            </div>
          </div>
        </div>

        <div className={SEC}>
          <div className={SEC_HEAD}>
            <div className={SEC_TITLE}>Personal details</div>
            <Button tone="p" small onClick={() => openSheet(<ProfileSheet member={me} />)}>
              Edit profile
            </Button>
          </div>
          <div className={FACT_GRID}>
            <Fact k="Mobile" v={me.phone} mono />
            <Fact k="Email" v={me.email} mono />
            <Fact k="Date of birth" v={me.dob ? formatDate(me.dob) : ''} mono />
            <Fact k="Age" v={age !== null ? String(age) : ''} mono />
            <Fact
              k="Gender"
              v={me.gender === 'M' ? 'Male' : me.gender === 'F' ? 'Female' : me.gender ? 'Other' : ''}
            />
            <Fact k="Home ground" v={grounds[me.ground] ?? ''} />
            <Fact k="T-shirt size" v={me.tshirt} />
          </div>
        </div>

        <div className={SEC}>
          <div className={SEC_HEAD}>
            <div className={SEC_TITLE}>Sign-in</div>
            <Button tone="p" small onClick={() => openSheet(<PasswordSheet member={me} />)}>
              {me.hasPassword ? 'Change password' : 'Set a password'}
            </Button>
          </div>
          <div className={FACT_GRID}>
            <Fact k="Sign in with" v="Mobile or email + password" />
            <Fact k="Password" v={me.hasPassword ? 'Set' : 'Not set yet'} />
            <Fact k="Reset link goes to" v={me.email ? maskEmail(me.email) : ''} mono />
          </div>
          {!me.hasPassword && (
            <div className={LOCK}>
              <div className={LOCK_ICON}>!</div>
              <div className={LOCK_TEXT}>
                You have no password yet. <b>Set one</b> — your mobile number or your email plus
                that password is all you need to sign in.
              </div>
            </div>
          )}
          {me.hasPassword && me.mustChangePassword && (
            <div className={LOCK}>
              <div className={LOCK_ICON}>!</div>
              <div className={LOCK_TEXT}>
                You are still on the password the <b>committee generated</b> for you.{' '}
                <b>Change it</b> to one only you know.
              </div>
            </div>
          )}
          {!me.email && (
            <div className={LOCK}>
              <div className={LOCK_ICON}>!</div>
              <div className={LOCK_TEXT}>
                <b>No email on file.</b> Add one from Edit profile — it is the only way to
                recover a forgotten password, and without it you would be locked out for good.
              </div>
            </div>
          )}
        </div>

        <div className={SEC}>
          <div className={SEC_HEAD}>
            <div className={SEC_TITLE}>Emergency &amp; medical</div>
          </div>
          <div className={FACT_GRID}>
            <Fact k="Blood group" v={me.blood} mono />
            <Fact k={isMinor ? 'Guardian' : 'Emergency contact'} v={me.emName} />
            <Fact k="Relationship" v={me.emRel || (isMinor ? 'Guardian' : '')} />
          </div>

          {me.medical && (
            <div className={`${FACT_GRID} mt-3.5`}>
              <Fact k="Medical notes" v={me.medical} />
            </div>
          )}

          {/* The number itself is deliberately absent. */}
          <div className={LOCK}>
            <div className={LOCK_ICON}>
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <rect x="4" y="10" width="16" height="11" rx="2.5" />
                <path d="M8 10V7a4 4 0 0 1 8 0v3" />
              </svg>
            </div>
            <div className={LOCK_TEXT}>
              <b>Your emergency number is private.</b> Only the club committee can see it, and only
              to reach your family if something happens during a run. Other members cannot view it.
            </div>
          </div>
        </div>

        <div className={SEC}>
          <div className={SEC_HEAD}>
            <div className={SEC_TITLE}>Membership</div>
          </div>
          <div className={FACT_GRID}>
            <Fact k="Tier" v={`${tier.label} — ₹${tier.fee}`} />
            <Fact
              k="Paid until"
              v={me.tier === 'life' ? 'Life member' : me.duesPaidTill ? formatDate(me.duesPaidTill) : ''}
              mono
            />
            <Fact k="Status" v={duesState(me).label} />
          </div>

          <div className={LOCK}>
            <div className={`${LOCK_ICON} font-market text-[11px]`}>?</div>
            <div className={LOCK_TEXT}>
              Need to change your name, bib number or membership tier?{' '}
              <b>Contact the committee</b> — send a message from your Notices tab.
            </div>
          </div>
        </div>
      </div>

      {/* Quietly reassures a member that the club has the roster on file. */}
      <div className="text-center font-market text-[10px] text-copy-label">
        {db.members.length} runners on the roster · Tirupur Runners est. 2013
      </div>
    </>
  );
}
